import React, { useEffect, useState } from "react";
import logger from "../lib/logger";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eyebrow, Btn } from "../components/Primitives";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { getSessionId } from "../lib/session";

const CARD_RISE_INIT = { opacity: 0, y: 8 };
const CARD_RISE_ANIM = { opacity: 1, y: 0 };

export default function Tiers() {
    const [tiers, setTiers] = useState([]);
    const [founding, setFounding] = useState({ spots_total: 100, spots_claimed: 0, spots_remaining: 100, closed: false });
    const [loading, setLoading] = useState(true);
    const [joined, setJoined] = useState({});
    const [emailDraft, setEmailDraft] = useState("");
    const [pendingTier, setPendingTier] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [r, mine, stats] = await Promise.all([
                    api.tiers(),
                    api.myWaitlist(getSessionId()).catch(() => ({ items: [] })),
                    api.foundingStats().catch(() => null),
                ]);
                setTiers(r.items || []);
                if (stats) setFounding(stats);
                const j = {};
                (mine.items || []).forEach(w => { j[w.tier_id] = true; });
                setJoined(j);
            } catch (err) {
                logger.error("tiers load failed:", err);
                toast.error("Could not load membership tiers");
            }
            setLoading(false);
        })();
    }, []);

    const foundersClub = tiers.find(t => t.id === "founders-club");
    const futureTiers = tiers.filter(t => t.id !== "founders-club");

    const submitJoin = async (tier_id, email) => {
        setSubmitting(true);
        try {
            await api.joinWaitlist({
                tier_id,
                session_id: getSessionId(),
                email: email || undefined,
            });
            setJoined(prev => ({ ...prev, [tier_id]: true }));
            setPendingTier(null);
            setEmailDraft("");
            toast.success("You're on the waitlist");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not join the waitlist");
        } finally {
            setSubmitting(false);
        }
    };

    const handleJoinClick = async (tier) => {
        if (joined[tier.id] || submitting) return;
        // If we already have an email on file, submit immediately
        try {
            const u = await api.getUser(getSessionId());
            if (u?.email) {
                await submitJoin(tier.id, u.email);
                return;
            }
        } catch (err) {
            logger.warn("user lookup failed:", err);
        }
        // Otherwise ask for email
        setPendingTier(tier);
    };

    return (
        <AppShell>
            <div className="flex-1 flex flex-col bg-cream" data-testid="tiers-screen">
                <div className="px-6 pt-6 pb-3 flex items-center justify-between border-b border-burgundy/10">
                    <Link to="/" data-testid="tiers-back-link" className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted hover:text-burgundy">
                        ← Back
                    </Link>
                    <span className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Membership</span>
                    <span className="w-8" />
                </div>

                <div className="px-7 pt-8">
                    <Eyebrow>Membership</Eyebrow>
                    <h1 className="mt-2 font-display text-4xl text-burgundy leading-[1.1]">
                        <span className="italic">One</span> live offer.
                    </h1>
                </div>

                {/* ---- Above the fold: Founder's Club — the only live offer ---- */}
                {!loading && foundersClub && (
                    <div className="px-6 pt-6">
                        <motion.div
                            initial={CARD_RISE_INIT}
                            animate={CARD_RISE_ANIM}
                            data-testid="founders-club-hero"
                            className="bg-burgundy text-cream p-6 sm:p-8"
                        >
                            <div className="text-[10px] tracking-[0.36em] uppercase text-gold">Live now</div>
                            <h2 className="mt-2 font-display text-3xl sm:text-4xl leading-tight">
                                <span className="italic">Founder's</span> Club
                            </h2>
                            <div className="mt-2 font-display text-xl text-gold" data-testid="founders-club-price">
                                $99 prepaid · <span data-testid="founders-club-claimed">{founding.spots_claimed}</span> of {founding.spots_total || 100} claimed
                            </div>
                            <ul className="mt-5 space-y-2 text-sm text-cream/95 leading-snug">
                                {foundersClub.features.map((f) => (
                                    <li key={f} className="flex gap-2">
                                        <span className="text-gold">—</span>
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            {foundersClub.fine_print && (
                                <p className="mt-4 text-[10px] tracking-[0.12em] text-cream/70 leading-snug">
                                    {foundersClub.fine_print}
                                </p>
                            )}
                            <button
                                data-testid="founders-club-cta"
                                onClick={() => navigate("/")}
                                disabled={founding.closed}
                                className="mt-6 w-full bg-gold text-burgundy uppercase text-xs tracking-[0.22em] py-4 hover:bg-gold-deep transition-colors disabled:opacity-60"
                            >
                                {founding.closed ? "Founding 100 is closed" : "Claim my founding spot — $99"}
                            </button>
                        </motion.div>
                    </div>
                )}

                {/* ---- Below: future membership ---- */}
                <div className="px-7 pt-10">
                    <Eyebrow>Coming at launch</Eyebrow>
                    <h2 className="mt-2 font-display text-2xl text-burgundy leading-tight">
                        What membership <span className="italic">will look like.</span>
                    </h2>
                    <p className="mt-3 text-sm text-ink-muted leading-snug">
                        These tiers open at official launch. Prices are provisional. Founding members lock in 25% below whatever public pricing becomes — join a waitlist and we'll notify you the moment your tier opens.
                    </p>
                </div>

                <div className="flex-1 px-6 py-7 space-y-4">
                    {loading && (
                        <div className="text-center text-sm text-ink-muted py-12">Loading tiers…</div>
                    )}

                    {!loading && futureTiers.map((t, i) => (
                        <motion.div
                            key={t.id}
                            initial={CARD_RISE_INIT}
                            animate={CARD_RISE_ANIM}
                            transition={{ delay: i * 0.05 }}
                            data-testid={`tier-card-${t.id}`}
                            className={`border p-5 ${t.highlight ? "border-burgundy bg-cream-soft" : "border-burgundy/15 bg-cream"}`}
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <h2 className="font-display text-2xl text-burgundy leading-tight">
                                    {t.name}
                                </h2>
                            </div>
                            <p className="mt-1 text-sm font-display italic text-ink-muted leading-snug">
                                {t.tagline}
                            </p>
                            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                                <span className="text-[10px] tracking-[0.32em] uppercase text-gold-deep" data-testid={`tier-price-${t.id}`}>
                                    {t.price_label}
                                </span>
                                {t.original_price_label && (
                                    <span className="text-[10px] tracking-[0.2em] uppercase text-ink-light line-through" data-testid={`tier-original-price-${t.id}`}>
                                        {t.original_price_label}
                                    </span>
                                )}
                                {t.beta_note && (
                                    <span className="text-[9px] tracking-[0.18em] uppercase bg-gold/25 text-burgundy px-1.5 py-0.5" data-testid={`tier-beta-note-${t.id}`}>
                                        {t.beta_note}
                                    </span>
                                )}
                            </div>
                            <ul className="mt-4 space-y-1.5 text-sm text-ink">
                                {t.features.map((f) => (
                                    <li key={f} className="flex gap-2">
                                        <span className="text-gold-deep">·</span>
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            {t.fine_print && (
                                <p className="mt-3 text-[10px] tracking-[0.12em] text-ink-light leading-snug">
                                    {t.fine_print}
                                </p>
                            )}
                            <div className="mt-5">
                                <Btn
                                    data-testid={`tier-join-btn-${t.id}`}
                                    variant={t.highlight ? "primary" : "secondary"}
                                    disabled={joined[t.id] || submitting}
                                    onClick={() => handleJoinClick(t)}
                                >
                                    {joined[t.id] ? "✓ On the waitlist" : "Join waitlist"}
                                </Btn>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {pendingTier && (
                    <div
                        className="fixed inset-0 bg-black/50 z-[80] flex items-end sm:items-center justify-center px-3 pb-3 sm:p-6"
                        onClick={() => !submitting && setPendingTier(null)}
                        data-testid="tiers-email-modal"
                    >
                        <div onClick={e => e.stopPropagation()} className="w-full sm:max-w-md bg-cream border border-burgundy/20">
                            <div className="px-6 pt-6 pb-3 border-b border-burgundy/15">
                                <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">{pendingTier.name}</div>
                                <h3 className="mt-1 font-display text-2xl text-burgundy">
                                    <span className="italic">Where</span> should we reach you?
                                </h3>
                                <p className="mt-2 text-sm text-ink-muted">
                                    We'll email you when {pendingTier.name} opens.
                                </p>
                            </div>
                            <div className="px-6 py-4">
                                <input
                                    data-testid="tiers-email-input"
                                    type="email"
                                    placeholder="you@example.com"
                                    autoFocus
                                    value={emailDraft}
                                    onChange={e => setEmailDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") submitJoin(pendingTier.id, emailDraft.trim()); }}
                                    className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                                />
                            </div>
                            <div className="px-6 py-4 border-t border-burgundy/15 flex gap-2">
                                <button
                                    onClick={() => setPendingTier(null)}
                                    disabled={submitting}
                                    className="flex-1 border border-burgundy/30 text-burgundy uppercase text-[11px] tracking-[0.22em] py-3"
                                >
                                    Cancel
                                </button>
                                <button
                                    data-testid="tiers-email-submit"
                                    disabled={submitting || !emailDraft.trim()}
                                    onClick={() => submitJoin(pendingTier.id, emailDraft.trim())}
                                    className="flex-1 bg-burgundy text-cream uppercase text-[11px] tracking-[0.22em] py-3 hover:bg-burgundy-deep disabled:opacity-50"
                                >
                                    {submitting ? "Joining…" : "Join waitlist"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
